import logger from '@/lib/logger';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { IDeviceService } from '@/services/device/IDeviceService';
import { DeviceService } from '@/services/device/DeviceService';
import { MockDeviceService } from '@/services/device/MockDeviceService';
import { BleDeviceService } from '@/services/device/BleDeviceService';

/**
 * Device Service Context
 *
 * Provides device service instance to all child components.
 * Service implementation (real vs mock) is determined at provider level.
 */
const DeviceServiceContext = createContext<IDeviceService | null>(null);

interface DeviceServiceProviderProps {
  mode: 'real' | 'mock';
  /**
   * Which real-device transport to use when `mode === 'real'`. Ignored in mock mode.
   * Defaults to `'usb'` so every existing caller (which never passes this prop) keeps
   * getting the same `DeviceService` instance as before — this is purely an additive
   * transport-selection seam, not a replacement for `mode`.
   */
  transport?: 'usb' | 'ble';
  children: ReactNode;
}

/**
 * DeviceServiceProvider Component
 *
 * Wraps the application and provides device service to all children.
 *
 * @example
 * <DeviceServiceProvider mode={mockMode ? 'mock' : 'real'}>
 *   <App />
 * </DeviceServiceProvider>
 *
 * @example
 * // Real BLE transport instead of the USB default:
 * <DeviceServiceProvider mode="real" transport="ble">
 *   <App />
 * </DeviceServiceProvider>
 */
export function DeviceServiceProvider({ mode, transport = 'usb', children }: DeviceServiceProviderProps) {
  const service = useMemo(() => {
    logger.debug('[DeviceServiceProvider] Creating service in mode:', mode, 'transport:', transport);
    if (mode === 'mock') {
      return new MockDeviceService();
    }
    return transport === 'ble' ? BleDeviceService.getInstance() : DeviceService.getInstance();
  }, [mode, transport]);

  // useMemo has no cleanup phase, so if mode/transport changes while a service is
  // actively connected, the previous instance's connection would otherwise never be
  // torn down. Disconnect it once the new instance has taken over.
  const previousServiceRef = useRef<IDeviceService | null>(null);
  useEffect(() => {
    const previous = previousServiceRef.current;
    if (previous && previous !== service) {
      logger.debug('[DeviceServiceProvider] mode/transport changed — disconnecting previous service instance');
      previous.disconnect().catch((err) => {
        logger.warn('[DeviceServiceProvider] Failed to disconnect previous service instance', err);
      });
    }
    previousServiceRef.current = service;
  }, [service]);

  return (
    <DeviceServiceContext.Provider value={service}>
      {children}
    </DeviceServiceContext.Provider>
  );
}

/**
 * useDeviceService Hook
 * 
 * Access the device service from any component within DeviceServiceProvider.
 * 
 * @throws Error if used outside of DeviceServiceProvider
 * 
 * @example
 * function MyComponent() {
 *   const deviceService = useDeviceService();
 *   
 *   const handleMeasure = async () => {
 *     const result = await deviceService.runMeasurement({...});
 *   };
 * }
 */
export function useDeviceService(): IDeviceService {
  const service = useContext(DeviceServiceContext);
  
  if (!service) {
    throw new Error(
      'useDeviceService must be used within DeviceServiceProvider. ' +
      'Make sure your component is wrapped in <DeviceServiceProvider>.'
    );
  }
  
  return service;
}
